/**
 * WebMCP Browser API Integration
 * Exposes platform context and client-side agent tools to AI browsing agents
 * (navigator.modelContext / window.modelContext / window.__EM_WEBMCP__)
 */

import { FAQ_ITEMS } from '../data/articles';
import { getPropertySlug } from './slugify';

export const registerWebMCP = ({ properties = [], onSelectProperty, onNavigate }) => {
  if (typeof window === 'undefined') return;

  const toolsList = [
    {
      name: 'buscar_propiedades',
      description: 'Busca y filtra casas, terrenos, departamentos y locales comerciales en venta o renta en Hidalgo del Parral, Chihuahua y alrededores.',
      inputSchema: {
        type: 'object',
        properties: {
          operationType: {
            type: 'string',
            enum: ['Venta', 'Renta', 'Todos'],
            description: 'Modalidad de operación inmobiliaria'
          },
          propertyType: {
            type: 'string',
            enum: ['Casa', 'Terreno', 'Bodega', 'Quinta', 'Locales comerciales', 'Departamentos', 'Todos'],
            description: 'Tipo de inmueble buscado'
          },
          neighborhood: {
            type: 'string',
            description: 'Colonia o zona en Parral (ej. Las Quintas, Bellavista, Centro, etc.)'
          },
          maxPriceMXN: {
            type: 'number',
            description: 'Presupuesto máximo en Pesos Mexicanos (MXN)'
          }
        }
      },
      execute: async ({ operationType, propertyType, neighborhood, maxPriceMXN } = {}) => {
        let results = [...properties];
        if (operationType && operationType !== 'Todos') {
          results = results.filter(p => (p.operationType || p.operation || '').toLowerCase() === operationType.toLowerCase());
        }
        if (propertyType && propertyType !== 'Todos') {
          results = results.filter(p => (p.propertyType || p.type || '').toLowerCase() === propertyType.toLowerCase());
        }
        if (neighborhood) {
          const q = neighborhood.toLowerCase();
          results = results.filter(p => (p.location?.neighborhood || p.location?.colonia || '').toLowerCase().includes(q));
        }
        if (maxPriceMXN && Number(maxPriceMXN) > 0) {
          results = results.filter(p => Number(p.priceMXN || p.price || 0) <= Number(maxPriceMXN));
        }
        return {
          totalFound: results.length,
          properties: results.map(p => ({
            id: p.id,
            slug: getPropertySlug(p),
            title: p.title,
            priceMXN: p.priceMXN || p.price,
            operation: p.operationType || p.operation,
            type: p.propertyType || p.type,
            status: p.status,
            colonia: p.location?.neighborhood || p.location?.colonia,
            url: `https://asesoriasem.com/#propiedad/${getPropertySlug(p)}`
          }))
        };
      }
    },
    {
      name: 'obtener_detalle_propiedad',
      description: 'Obtiene las características completas, precio en MXN, dimensiones y estatus legal de una propiedad específica por su ID o slug.',
      inputSchema: {
        type: 'object',
        required: ['identifier'],
        properties: {
          identifier: {
            type: 'string',
            description: 'ID o slug de la propiedad en el catálogo'
          }
        }
      },
      execute: async ({ identifier }) => {
        const found = properties.find(p => p.id === identifier || getPropertySlug(p) === identifier || p.slug === identifier);
        if (!found) return { found: false, error: 'Propiedad no encontrada en el inventario activo.' };
        return {
          found: true,
          property: {
            ...found,
            slug: getPropertySlug(found),
            url: `https://asesoriasem.com/#propiedad/${getPropertySlug(found)}`
          }
        };
      }
    },
    {
      name: 'consultar_faq_legal',
      description: 'Consulta respuestas jurídicas oficiales sobre escrituración notarial, créditos Infonavit, juicios sucesorios, ISR e ISAI en Chihuahua.',
      inputSchema: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            description: 'Término de búsqueda (ej. escrituras, infonavit, herencia, isai, isr)'
          }
        }
      },
      execute: async ({ query = '' } = {}) => {
        const q = (query || '').toLowerCase().trim();
        if (!q) return { faqs: FAQ_ITEMS };
        const matches = FAQ_ITEMS.filter(f =>
          f.question.toLowerCase().includes(q) ||
          f.answer.toLowerCase().includes(q) ||
          f.category.toLowerCase().includes(q)
        );
        return {
          query: q,
          totalMatches: matches.length,
          matches
        };
      }
    },
    {
      name: 'navegar_a_propiedad',
      description: 'Navega la interfaz visual del usuario hacia la ficha detallada de una propiedad.',
      inputSchema: {
        type: 'object',
        required: ['identifier'],
        properties: {
          identifier: {
            type: 'string',
            description: 'Slug o ID de la propiedad'
          }
        }
      },
      execute: async ({ identifier }) => {
        if (onSelectProperty) {
          onSelectProperty(identifier);
          return { success: true, message: `Navegando a la propiedad ${identifier}` };
        }
        return { success: false, message: 'Función de navegación no disponible en este contexto.' };
      }
    },
    {
      name: 'navegar_a_seccion',
      description: 'Navega la interfaz visual hacia una sección de la plataforma (home, properties, sell, guide, about).',
      inputSchema: {
        type: 'object',
        required: ['seccion'],
        properties: {
          seccion: {
            type: 'string',
            enum: ['home', 'properties', 'sell', 'guide', 'about'],
            description: 'Identificador de la sección'
          }
        }
      },
      execute: async ({ seccion }) => {
        if (onNavigate) {
          onNavigate(seccion);
          return { success: true, message: `Navegando a la sección ${seccion}` };
        }
        return { success: false, message: 'Función de navegación no disponible en este contexto.' };
      }
    }
  ];

  const webMCPContext = {
    name: 'EM Asesoría Inmobiliaria y Jurídica',
    version: '1.0.0',
    description: 'Plataforma oficial de bienes raíces y consultoría jurídica en Hidalgo del Parral, Chihuahua, dirigida por el Lic. Edgar Molina.',
    officialContact: {
      representative: 'Licenciado Edgar Molina',
      phone: '627 279 2964',
      phoneIntl: '+526272792964',
      email: 'juridico@asesoriasem.com',
      address: 'Hidalgo del Parral, Chihuahua, México',
      coverage: ['Parral', 'Santa Bárbara', 'San Francisco del Oro', 'Valle de Allende', 'Matamoros', 'Jiménez'],
      currency: 'MXN'
    },
    tools: toolsList
  };

  // Provide to standard navigator.modelContext if supported by browser/agent
  if (typeof navigator !== 'undefined') {
    if (typeof navigator.modelContext?.provideContext === 'function') {
      try {
        navigator.modelContext.provideContext({
          tools: toolsList,
          context: webMCPContext
        });
      } catch (err) {
        console.debug('[WebMCP] navigator.modelContext.provideContext:', err);
      }
    } else {
      // Polyfill navigator.modelContext
      navigator.modelContext = {
        ...(navigator.modelContext || {}),
        tools: toolsList,
        context: webMCPContext,
        provideContext: (ctx) => {
          if (ctx?.tools) navigator.modelContext.tools = ctx.tools;
          if (ctx?.context) navigator.modelContext.context = { ...webMCPContext, ...ctx.context };
        }
      };
    }
  }

  // Global window object for browser agent inspection
  window.modelContext = webMCPContext;
  window.__EM_WEBMCP__ = webMCPContext;

  console.log('[WebMCP] Contexto y 5 herramientas WebMCP registradas con éxito para agentes de IA.');
};

export default registerWebMCP;
